import { allCategoryGroupNames } from 'config/filters'
import { SavedFilters } from 'reducers/filtering/saved'
import { CategoryGroupName, Filter } from 'typings/filters'

export enum DeprecatedCategoryGroupName {
  Base = 'Base',
  Audience = 'Audience',
  Product = 'Product Specification',
}

export type ImportedFilter = Omit<Filter, 'categoryGroup'> & {
  categoryGroup: CategoryGroupName | DeprecatedCategoryGroupName
}

export type ImportedFilterSet = {
  id?: string
  name: string
  filters: Partial<Record<CategoryGroupName | DeprecatedCategoryGroupName, ImportedFilter[]>>
  isApplied?: boolean
  isDisabled?: boolean
}

const shopperFilterVariableIDs = [
  // Demographics
  6, 8, 37, 9, 34, 32, 33, 11, 10, 59, 121,

  // Influential Shoppers
  52, 53,
]
const isDeprecatedAudienceGroup = (
  hasDeprecatedAudienceFilters: boolean,
  groupName: CategoryGroupName,
  predicate: (groupName: CategoryGroupName) => boolean
): boolean => {
  return hasDeprecatedAudienceFilters && predicate(groupName)
}
const finalFilters = (filtersToSave: ImportedFilterSet, groupName: CategoryGroupName) => {
  return filtersToSave.filters[groupName] ?? []
}
const hasDeprecatedFilters = (deprecatedAudienceFilters: ImportedFilter[] | undefined) => {
  return deprecatedAudienceFilters && deprecatedAudienceFilters.length > 0
}
export const formatSavedFilters = (importedFilterSet: ImportedFilterSet[]): SavedFilters => {
  if (!importedFilterSet) return []

  return importedFilterSet.map((filtersToSave) => {
    const deprecatedAudienceFilters = filtersToSave.filters[DeprecatedCategoryGroupName.Audience]
    const hasDeprecatedAudienceFilters = hasDeprecatedFilters(deprecatedAudienceFilters)

    const deprecatedBaseFilters = filtersToSave.filters[DeprecatedCategoryGroupName.Base]
    const deprecatedProductFilters = filtersToSave.filters[DeprecatedCategoryGroupName.Product]

    return {
      name: filtersToSave.name,
      isApplied: filtersToSave.isApplied,
      isDisabled: filtersToSave.isDisabled,
      id: filtersToSave.id,
      filters: allCategoryGroupNames.reduce(
        (acc, groupName) => {
          // Deprecated Category Group: Audience
          const isGeneralShopper = (groupName: CategoryGroupName): boolean => {
            return groupName === CategoryGroupName.General_Shopper
          }

          const isGeneralPurchase = (groupName: CategoryGroupName): boolean => {
            return groupName === CategoryGroupName.General_Purchase
          }
          if (
            isDeprecatedAudienceGroup(
              hasDeprecatedAudienceFilters as boolean,
              groupName,
              isGeneralShopper
            )
          ) {
            const shopperFilters = (deprecatedAudienceFilters as ImportedFilter[])
              .filter((filter) => shopperFilterVariableIDs.includes(filter.variable_id))
              .map((filter) => ({ ...filter, categoryGroup: groupName }))
            return {
              ...acc,
              [groupName]: shopperFilters,
            }
          }
          if (
            isDeprecatedAudienceGroup(
              hasDeprecatedAudienceFilters as boolean,
              groupName,
              isGeneralPurchase
            )
          ) {
            const purchaseFilters = (deprecatedAudienceFilters as ImportedFilter[])
              .filter((filter) => !shopperFilterVariableIDs.includes(filter.variable_id))
              .map((filter) => ({ ...filter, categoryGroup: groupName }))
            return {
              ...acc,
              [groupName]: purchaseFilters,
            }
          }

          // Deprecated Category Group: Base
          if (deprecatedBaseFilters && groupName === CategoryGroupName.General_Base) {
            return {
              ...acc,
              [groupName]: deprecatedBaseFilters.map((filter) => ({
                ...filter,
                categoryGroup: groupName,
              })),
            }
          }

          // Deprecated Category Group: Product Specification
          if (deprecatedProductFilters && groupName === CategoryGroupName.General_Product) {
            return {
              ...acc,
              [groupName]: deprecatedProductFilters.map((filter) => ({
                ...filter,
                categoryGroup: groupName,
              })),
            }
          }

          return {
            ...acc,
            [groupName]: finalFilters(filtersToSave, groupName),
          }
        },
        {} as Record<CategoryGroupName, Filter[]>
      ),
    }
  })
}
